/**
 * Static data for the Game Pass → Configuration → Seasons listing page.
 * The filter tabs and per-season cards both consume types declared here so
 * that, when the seasons API lands, the only thing that changes is the
 * data source — `SeasonsList` continues to render the same `SeasonData`
 * shape into the same reusable `SeasonCard`.
 *
 * Keys must stay aligned with whatever the backend ends up using for the
 * `status` parameter (string-encoded enum), so we keep them as URL-safe
 * lowercase tokens rather than free-form display copy.
 */

import dayjs, { type Dayjs } from "dayjs";

import { AppStrings } from "../../../utils/constants/AppStrings";
import { SEASONS_FILTER_ID } from "../../../types/gamePass";
import type {
  SeasonCountdown,
  SeasonData,
  SeasonMetric,
  SeasonStatus,
  SeasonStatusTone,
  SeasonsFilterId,
  SeasonsFilterTab,
  SeasonTaskSubmissionMode,
  SeasonTrailingKind,
} from "../../../types/gamePass";
import type {
  ChallengeDraft,
  RewardDraft,
  ScheduleValues,
  SeasonTiersValue,
  TaskDraft,
  TaskTriggerDraft,
} from "../CreateSeason/createSeasonData";

// The UI model types (`SeasonData`, `SeasonStatus`, …) have moved to
// `src/types/gamePass.ts` so the query layer
// (`src/queries/game-pass/seasonsMappers.ts`) can depend on them
// without importing from the component layer. They are re-exported
// here so existing call sites that already pull them from this
// module keep compiling untouched.
export type {
  SeasonCountdown,
  SeasonData,
  SeasonMetric,
  SeasonStatus,
  SeasonStatusTone,
  SeasonsFilterId,
  SeasonsFilterTab,
  SeasonTrailingKind,
};

export const SEASONS_FILTER_TABS: ReadonlyArray<SeasonsFilterTab> = [
  { id: SEASONS_FILTER_ID.TIMELINE, label: AppStrings.SEASONS_FILTER_TIMELINE },
  { id: SEASONS_FILTER_ID.ALL, label: AppStrings.SEASONS_FILTER_ALL },
  { id: SEASONS_FILTER_ID.SCHEDULED, label: AppStrings.SEASONS_FILTER_SCHEDULED },
  { id: SEASONS_FILTER_ID.DRAFTS, label: AppStrings.SEASONS_FILTER_DRAFTS },
  { id: SEASONS_FILTER_ID.COMPLETED, label: AppStrings.SEASONS_FILTER_COMPLETED },
  { id: SEASONS_FILTER_ID.CANCELED, label: AppStrings.SEASONS_FILTER_CANCELED },
];

export const DEFAULT_SEASONS_FILTER: SeasonsFilterId =
  SEASONS_FILTER_ID.TIMELINE;

/** URL search-param name the listing uses to persist the active filter
 *  tab. Centralised so the listing (writer) and Season Details (reader,
 *  for the back-navigation round-trip) stay in lockstep — changing the
 *  param key here updates every consumer in one place. */
export const SEASONS_FILTER_QUERY_PARAM = "filter";

/** Narrow an arbitrary string (URL search param, location state, etc.)
 *  into a known `SeasonsFilterId`. Returns `null` for unknown values so
 *  callers can fall back to their own default (the listing falls back
 *  to `DEFAULT_SEASONS_FILTER`). Centralised so any caller that needs
 *  to validate a filter id reads from the same source of truth as the
 *  tab strip — adding a new tab to `SEASONS_FILTER_TABS` automatically
 *  widens the set of values this helper accepts. */
export function parseFilterId(
  value: string | null | undefined,
): SeasonsFilterId | null {
  if (!value) return null;
  const match = SEASONS_FILTER_TABS.find((tab) => tab.id === value);
  return match ? match.id : null;
}

// ─── Season card model ────────────────────────────────────────────────────
//
// The presentational shapes consumed by `SeasonCard` (`SeasonData`,
// `SeasonStatus`, `SeasonCountdown`, `SeasonMetric`, `SeasonTrailingKind`)
// live in `src/types/gamePass.ts` and are re-exported at the top of
// this module so existing imports keep compiling. The fixtures below
// continue to seed the listing until every consumer is wired to the
// API.

/** Placeholder used by `SeasonsList` until the seasons API ships. Mirrors
 *  the active-season frame from the Figma spec one-for-one — kept here
 *  (not inlined into the page) so future API mappers can swap it out by
 *  replacing exactly one import. */
export const MOCK_ACTIVE_SEASON: SeasonData = {
  id: "mock-may-2026",
  name: "May 2026 Season",
  dateRange: "May 1 - May 31, 2026",
  countdown: {
    prefix: AppStrings.SEASON_COUNTDOWN_ENDS_IN,
    value: "24 days",
  },
  metrics: [
    {
      id: "challenges",
      label: AppStrings.SEASON_METRIC_CHALLENGES,
      value: 6,
    },
    { id: "tasks", label: AppStrings.SEASON_METRIC_TASKS, value: 8 },
  ],
  status: {
    label: AppStrings.SEASON_STATUS_ACTIVE,
    tone: "active",
  },
  trailing: "locked",
};

/** Placeholder list used by the Scheduled column of `SeasonsList` until
 *  the seasons API ships. Mirrors the three scheduled cards from the
 *  Figma spec — Up-Next + Locked for the next-in-line season, followed
 *  by two editable upcoming seasons. Kept in this module (not the page)
 *  so a future API mapper can swap both `MOCK_ACTIVE_SEASON` and this
 *  array by replacing a single import. */
export const MOCK_SCHEDULED_SEASONS: ReadonlyArray<SeasonData> = [
  {
    id: "mock-summer-2026",
    name: "Summer 2026 Season",
    dateRange: "June 1 - Aug 31, 2026",
    countdown: {
      prefix: AppStrings.SEASON_COUNTDOWN_STARTS_IN,
      value: "24 days",
    },
    metrics: [
      {
        id: "challenges",
        label: AppStrings.SEASON_METRIC_CHALLENGES,
        value: 4,
      },
      { id: "tasks", label: AppStrings.SEASON_METRIC_TASKS, value: 12 },
    ],
    status: {
      label: AppStrings.SEASON_STATUS_UP_NEXT,
      tone: "upNext",
    },
    trailing: "locked",
  },
  {
    id: "mock-september-2026",
    name: "September 2026 Season",
    dateRange: "Sept 1 - Sept 30, 2026",
    countdown: {
      prefix: AppStrings.SEASON_COUNTDOWN_LOCKS_IN,
      value: "64 days",
    },
    metrics: [
      {
        id: "challenges",
        label: AppStrings.SEASON_METRIC_CHALLENGES,
        value: 6,
      },
      { id: "tasks", label: AppStrings.SEASON_METRIC_TASKS, value: 8 },
    ],
    trailing: "edit",
  },
  {
    id: "mock-fall-2026",
    name: "Fall 2026 Season",
    dateRange: "Oct 1 - Nov 31, 2026",
    countdown: {
      prefix: AppStrings.SEASON_COUNTDOWN_STARTS_IN,
      value: "80 days",
    },
    metrics: [
      {
        id: "challenges",
        label: AppStrings.SEASON_METRIC_CHALLENGES,
        value: 7,
      },
      { id: "tasks", label: AppStrings.SEASON_METRIC_TASKS, value: 10 },
    ],
    trailing: "edit",
  },
];

/** Placeholder list used by the Drafts column of `SeasonsList` until the
 *  seasons API ships. Draft seasons are works-in-progress: they have a
 *  working name, tentative dates, and a top-right `Draft` status badge
 *  in the platform's amber tone (`PROVIDER_DISTRIBUTION_COLOR_6`) so
 *  the card matches the Figma spec at a glance. They intentionally
 *  carry no `countdown` (a draft is not yet on the calendar), and
 *  `trailing` is always `"edit"` so the pencil keeps the editor
 *  entrypoint visible. Kept in this module (not the page) so a future
 *  API mapper can swap drafts by replacing a single import, exactly
 *  like `MOCK_ACTIVE_SEASON` and `MOCK_SCHEDULED_SEASONS`. */
export const MOCK_DRAFT_SEASONS: ReadonlyArray<SeasonData> = [
  {
    id: "mock-winter-2026-draft",
    name: "Winter 2026 Holiday Season",
    dateRange: "Dec 1 - Dec 31, 2026",
    metrics: [
      {
        id: "challenges",
        label: AppStrings.SEASON_METRIC_CHALLENGES,
        value: 3,
      },
      { id: "tasks", label: AppStrings.SEASON_METRIC_TASKS, value: 5 },
    ],
    status: {
      label: AppStrings.SEASON_STATUS_DRAFT,
      tone: "draft",
    },
    trailing: "edit",
  },
  {
    id: "mock-new-year-2027-draft",
    name: "New Year 2027 Kickoff",
    dateRange: "Jan 1 - Jan 31, 2027",
    metrics: [
      {
        id: "challenges",
        label: AppStrings.SEASON_METRIC_CHALLENGES,
        value: 2,
      },
      { id: "tasks", label: AppStrings.SEASON_METRIC_TASKS, value: 4 },
    ],
    status: {
      label: AppStrings.SEASON_STATUS_DRAFT,
      tone: "draft",
    },
    trailing: "edit",
  },
];

/** Placeholder list used by the Completed column of `SeasonsList` until
 *  the seasons API ships. Completed seasons have already elapsed, so the
 *  data is intentionally light:
 *  - top-right `Completed` badge in the muted grey tone (distinct from
 *    the active green dot, the up-next violet, and the draft amber);
 *  - no `countdown` — a finished season is no longer on the clock;
 *  - `trailing: "seeReports"` so the card renders the bottom-row link
 *    out to the per-season reports page (currently disabled — the
 *    reports route is not yet implemented).
 *
 *  Dates are deliberately in the past relative to the current Active
 *  season (May 2026), so the listing reads chronologically and the
 *  mock data communicates the intended lifecycle ordering. Kept in
 *  this module (not the page) so a future API mapper can swap completed
 *  seasons by replacing a single import, exactly like the other mock
 *  arrays. */
export const MOCK_COMPLETED_SEASONS: ReadonlyArray<SeasonData> = [
  {
    id: "mock-april-2026-completed",
    name: "April 2026 Season",
    dateRange: "Apr 1 - Apr 30, 2026",
    metrics: [
      {
        id: "challenges",
        label: AppStrings.SEASON_METRIC_CHALLENGES,
        value: 6,
      },
      { id: "tasks", label: AppStrings.SEASON_METRIC_TASKS, value: 8 },
    ],
    status: {
      label: AppStrings.SEASON_STATUS_COMPLETED,
      tone: "completed",
    },
    trailing: "seeReports",
  },
  {
    id: "mock-march-2026-completed",
    name: "March 2026 Season",
    dateRange: "Mar 1 - Mar 31, 2026",
    metrics: [
      {
        id: "challenges",
        label: AppStrings.SEASON_METRIC_CHALLENGES,
        value: 5,
      },
      { id: "tasks", label: AppStrings.SEASON_METRIC_TASKS, value: 9 },
    ],
    status: {
      label: AppStrings.SEASON_STATUS_COMPLETED,
      tone: "completed",
    },
    trailing: "seeReports",
  },
  {
    id: "mock-february-2026-completed",
    name: "February 2026 Season",
    dateRange: "Feb 1 - Feb 28, 2026",
    metrics: [
      {
        id: "challenges",
        label: AppStrings.SEASON_METRIC_CHALLENGES,
        value: 4,
      },
      { id: "tasks", label: AppStrings.SEASON_METRIC_TASKS, value: 7 },
    ],
    status: {
      label: AppStrings.SEASON_STATUS_COMPLETED,
      tone: "completed",
    },
    trailing: "seeReports",
  },
  {
    id: "mock-january-2026-completed",
    name: "January 2026 Season",
    dateRange: "Jan 1 - Jan 31, 2026",
    metrics: [
      {
        id: "challenges",
        label: AppStrings.SEASON_METRIC_CHALLENGES,
        value: 5,
      },
      { id: "tasks", label: AppStrings.SEASON_METRIC_TASKS, value: 6 },
    ],
    status: {
      label: AppStrings.SEASON_STATUS_COMPLETED,
      tone: "completed",
    },
    trailing: "seeReports",
  },
  {
    id: "mock-december-2025-completed",
    name: "December 2025 Holiday Season",
    dateRange: "Dec 1 - Dec 31, 2025",
    metrics: [
      {
        id: "challenges",
        label: AppStrings.SEASON_METRIC_CHALLENGES,
        value: 7,
      },
      { id: "tasks", label: AppStrings.SEASON_METRIC_TASKS, value: 12 },
    ],
    status: {
      label: AppStrings.SEASON_STATUS_COMPLETED,
      tone: "completed",
    },
    trailing: "seeReports",
  },
  {
    id: "mock-november-2025-completed",
    name: "November 2025 Season",
    dateRange: "Nov 1 - Nov 30, 2025",
    metrics: [
      {
        id: "challenges",
        label: AppStrings.SEASON_METRIC_CHALLENGES,
        value: 6,
      },
      { id: "tasks", label: AppStrings.SEASON_METRIC_TASKS, value: 8 },
    ],
    status: {
      label: AppStrings.SEASON_STATUS_COMPLETED,
      tone: "completed",
    },
    trailing: "seeReports",
  },
];

// ─── Season Details (per-season view) ─────────────────────────────────────
//
// The Details page is reached by clicking a season card's title from the
// listing. It needs richer data than the card — full date range, audience
// stats, challenge cards (with their tasks/rewards), the sequential toggle,
// and whether the page should render in locked (Active) or editable
// (Scheduled / Draft) mode. We keep both shapes in this module so the
// future seasons-detail API swap is one import change for any consumer.

/** Re-exported under the listing module's old names so existing imports
 *  (and any future ones) keep compiling, but the canonical declarations
 *  now live in `createSeasonData.ts` so the wizard + modal share one
 *  source of truth. */
export type SeasonEditableTiers = SeasonTiersValue;
export type SeasonEditableSchedule = ScheduleValues;

/** Top-of-page "General" card content for the Season Details view. */
export type SeasonGeneral = {
  /** Full date range with hours, e.g. "June 1, 2026 (12:00am) - June 31, 2026 (11:59pm)". */
  fullDateRange: string;
  /** Audience metric — total players enrolled in this season. */
  enrolled: number;
  /** Audience metric — average completion percentage across all enrolled players. */
  avgCompletionPct: number;
  /** Reports count surfaced behind the "See all >" link. Drives the
   *  metric tile body even though we don't render the number explicitly
   *  today — kept for parity with the future reports route. */
  reportsCount: number;
  /** Whether the destructive "End Season" link is rendered on the title
   *  row. Only `true` for Active seasons. */
  endSeasonEnabled: boolean;
  /** Free-form admin description shown only in the edit modal (the
   *  read-only General card never surfaces it). Optional so existing
   *  mocks / future API payloads can omit it on locked seasons. */
  description?: string;
  /** Which tiers this season exposes — feeds the Free/Premium
   *  checkboxes in `EditSeasonModal`. Optional for the same reason as
   *  `description`. */
  tiers?: SeasonEditableTiers;
  /** Default sportsbook task submission mode — feeds Step 1 / Edit modal. */
  sportsbookSubmissionMode?: SeasonTaskSubmissionMode;
  /** Default casino task submission mode — locked to AUTO in the UI. */
  casinoSubmissionMode?: SeasonTaskSubmissionMode;
  /** S3 object key of a persisted season banner. `null` means the admin
   *  explicitly removed it. */
  imgKey?: string | null;
  /** Fully-qualified preview URL for a persisted season banner. */
  imageUrl?: string | null;
  /** Typed schedule values used to seed the date/time pickers in
   *  `EditSeasonModal`. The display string `fullDateRange` above stays
   *  the source of truth for the read-only General card so existing
   *  consumers aren't disturbed. */
  schedule?: SeasonEditableSchedule;
};

/** Top-level shape consumed by the Season Details page. */
export type SeasonDetailsData = {
  /** Header info — name, status, dates, countdown, etc. Mirrors what the
   *  card on the listing shows, so we can render breadcrumbs / sub-headers
   *  without re-deriving from any other source. */
  season: SeasonData;
  /** `locked` → read-only view (Active season). Challenges render as
   *  collapsed `ChallengeCard`s in locked mode. `editable` → reuses the
   *  full `ChallengesAndTasks` editor for Scheduled / Draft seasons. */
  mode: "locked" | "editable";
  general: SeasonGeneral;
  /** Seed value for the "users must complete challenges sequentially"
   *  toggle. Read-only in locked mode, editable otherwise. */
  sequential: boolean;
  /** Challenges (with nested tasks + rewards). Reuses the wizard's draft
   *  model so the same `ChallengeCard` renders unchanged in both views. */
  challenges: ReadonlyArray<ChallengeDraft>;
};

// ─── Helpers for building rich mock challenges ────────────────────────────
//
// These keep the inline mock map below readable. Each helper returns a fresh
// object literal so the consumer can spread / patch without poisoning the
// shared mock (callers like `SeasonDetails` keep their own local state).

const buildReward = (
  id: string,
  reward: Omit<RewardDraft, "id">,
): RewardDraft => ({ id, ...reward });

const buildTrigger = (
  overrides: Partial<TaskTriggerDraft> = {},
): TaskTriggerDraft => ({
  taskType: "SPORTSBOOK",
  betType: "ANY",
  betCurrencies: ["REBET_CASH"],
  qualifier: "WIN",
  ...overrides,
});

const buildTask = (
  id: string,
  task: Omit<TaskDraft, "id" | "trigger" | "rewards"> & {
    trigger?: Partial<TaskTriggerDraft>;
    rewards?: ReadonlyArray<RewardDraft>;
  },
): TaskDraft => ({
  id,
  name: task.name,
  description: task.description,
  trigger: buildTrigger(task.trigger),
  rewards: task.rewards ?? [
    buildReward(`${id}-r1`, { amount: "100", type: "XP" }),
  ],
});

const buildChallenge = (
  id: string,
  challenge: Omit<
    ChallengeDraft,
    "id" | "isExpanded" | "isSaved" | "rewards" | "tasks" | "pathAvailability"
  > & {
    rewards: ReadonlyArray<RewardDraft>;
    tasks: ReadonlyArray<TaskDraft>;
    pathAvailability?: ChallengeDraft["pathAvailability"];
  },
): ChallengeDraft => ({
  id,
  pathAvailability: challenge.pathAvailability ?? "FREE",
  name: challenge.name,
  description: challenge.description,
  rewards: challenge.rewards,
  tasks: challenge.tasks,
  // Mock challenges are pre-saved (so the summary view renders) and
  // start collapsed (Figma shows summary cards on first load).
  isSaved: true,
  isExpanded: false,
});

// ─── Per-season mock challenge sets ───────────────────────────────────────
//
// Each season gets its own set so different cards render visibly different
// content on click — confirming to the user that navigation is data-driven
// and not just a shared template.

const MAY_2026_CHALLENGES: ReadonlyArray<ChallengeDraft> = [
  buildChallenge("may-bronze", {
    name: "Bronze Challenge",
    description: "Easy starter bets to warm up the season.",
    rewards: [
      buildReward("may-bronze-r1", { amount: "50", type: "FREE_CASH_PICK" }),
      buildReward("may-bronze-r2", { amount: "500", type: "XP" }),
    ],
    tasks: [
      buildTask("may-bronze-t1", {
        name: "First Bet",
        description: "Place your first bet of the season.",
        trigger: { qualifier: "ANY_SETTLED" },
      }),
      buildTask("may-bronze-t2", {
        name: "Triple Threat",
        description: "Win three Rebet Cash bets back-to-back.",
        trigger: { qualifier: "WIN", streak: "3" },
      }),
      buildTask("may-bronze-t3", {
        name: "Parlay Play",
        description: "Place a parlay on any game.",
        trigger: {
          betType: "PARLAY",
          qualifier: "ANY_SETTLED",
        },
      }),
      buildTask("may-bronze-t4", {
        name: "Underdog Special",
        description: "Win on an underdog moneyline pick.",
        trigger: { qualifier: "WIN" },
      }),
      buildTask("may-bronze-t5", {
        name: "Cash Crusher",
        description: "Place a $20+ Rebet Cash bet.",
        trigger: { minBetAmount: "20", qualifier: "ANY_SETTLED" },
      }),
      buildTask("may-bronze-t6", {
        name: "Coins Collector",
        description: "Place a Rebet Coins wager and win.",
        trigger: { betCurrencies: ["REBET_COINS"], qualifier: "WIN" },
      }),
    ],
  }),
  buildChallenge("may-silver", {
    name: "Silver Challenge",
    description: "Sharpen your strategy with smarter bets.",
    rewards: [
      buildReward("may-silver-r1", { amount: "50", type: "FREE_CASH_PICK" }),
      buildReward("may-silver-r2", { amount: "500", type: "XP" }),
    ],
    tasks: [
      buildTask("may-silver-t1", {
        name: "Overtime Hero",
        description: "Predict a game to go into overtime and win your bet.",
        trigger: { qualifier: "WIN" },
        rewards: [
          buildReward("may-silver-t1-r1", { amount: "150", type: "XP" }),
        ],
      }),
      buildTask("may-silver-t2", {
        name: "Underdog Wizard",
        description: "Successfully bet on an underdog team to win against the odds.",
        trigger: { qualifier: "WIN" },
      }),
      buildTask("may-silver-t3", {
        name: "Streak Master",
        description: "Win bets on three consecutive games without a loss.",
        trigger: { qualifier: "WIN", streak: "3" },
      }),
      buildTask("may-silver-t4", {
        name: "Comeback King",
        description:
          "Win a bet on a team that was trailing at halftime.",
        trigger: { qualifier: "WIN" },
      }),
    ],
  }),
  buildChallenge("may-premium", {
    name: "Premium Challenge",
    description: "Competitive challenges for advanced players.",
    rewards: [
      buildReward("may-premium-r1", { amount: "50", type: "FREE_CASH_PICK" }),
      buildReward("may-premium-r2", { amount: "500", type: "XP" }),
    ],
    tasks: [
      buildTask("may-premium-t1", {
        name: "Parlay Pro",
        description: "Win a 4-leg parlay during the season.",
        trigger: { betType: "PARLAY", qualifier: "WIN" },
      }),
      buildTask("may-premium-t2", {
        name: "Big Spender",
        description: "Place a $100+ Rebet Cash bet and win.",
        trigger: { minBetAmount: "100", qualifier: "WIN" },
      }),
      buildTask("may-premium-t3", {
        name: "Sport Sampler",
        description: "Win a bet on hockey.",
        trigger: { qualifier: "WIN", sportSpecific: ["HOCKEY"] },
      }),
      buildTask("may-premium-t4", {
        name: "Push Master",
        description: "Land a push on a Rebet Cash bet.",
        trigger: { qualifier: "WIN_OR_PUSH" },
      }),
      buildTask("may-premium-t5", {
        name: "Season Bettor",
        description: "Place a qualifying bet during the season.",
        trigger: { qualifier: "ANY_SETTLED" },
      }),
      buildTask("may-premium-t6", {
        name: "Social Butterfly",
        description: "Share three winning bets to socials.",
        trigger: { taskType: "SOCIALS", qualifier: "WIN" },
      }),
      buildTask("may-premium-t7", {
        name: "Casino Crossover",
        description: "Place 5 casino wagers totalling $50+.",
        trigger: { taskType: "CASINO", minBetAmount: "50" },
      }),
      buildTask("may-premium-t8", {
        name: "Comeback King",
        description: "Win a bet after losing three in a row.",
        trigger: { qualifier: "WIN", streak: "3" },
      }),
    ],
  }),
  buildChallenge("may-legend", {
    name: "Legend Challenge",
    description: "Invitation-only elite competition.",
    rewards: [
      buildReward("may-legend-r1", { amount: "50", type: "FREE_CASH_PICK" }),
      buildReward("may-legend-r2", { amount: "500", type: "XP" }),
    ],
    tasks: Array.from({ length: 12 }, (_, idx) =>
      buildTask(`may-legend-t${idx + 1}`, {
        name: `Legend Trial ${idx + 1}`,
        description: "An invitation-only Legend tier challenge.",
        trigger: { qualifier: "WIN" },
      }),
    ),
  }),
];

const SUMMER_2026_CHALLENGES: ReadonlyArray<ChallengeDraft> = [
  buildChallenge("summer-warmup", {
    name: "Summer Warm-Up",
    description: "Hop into Summer with three intro bets.",
    rewards: [
      buildReward("summer-warmup-r1", { amount: "25", type: "FREE_CASH_PICK" }),
      buildReward("summer-warmup-r2", { amount: "250", type: "XP" }),
    ],
    tasks: [
      buildTask("summer-warmup-t1", {
        name: "First Splash",
        description: "Place your first Summer bet.",
        trigger: { qualifier: "ANY_SETTLED" },
      }),
      buildTask("summer-warmup-t2", {
        name: "Beach Parlay",
        description: "Place a 2-leg parlay.",
        trigger: { betType: "PARLAY", qualifier: "ANY_SETTLED" },
      }),
      buildTask("summer-warmup-t3", {
        name: "Sunset Win",
        description: "Win a bet on a late-evening match.",
        trigger: { qualifier: "WIN" },
      }),
    ],
  }),
  buildChallenge("summer-grand", {
    name: "Grand Slam Series",
    description: "Big-money summer wagers — for the regulars.",
    rewards: [
      buildReward("summer-grand-r1", { amount: "100", type: "FREE_CASH_PICK" }),
      buildReward("summer-grand-r2", { amount: "1000", type: "XP" }),
    ],
    tasks: [
      buildTask("summer-grand-t1", {
        name: "Centre Court",
        description: "Win a straight bet on a featured tennis match.",
        trigger: { betType: "SINGLE", qualifier: "WIN" },
      }),
      buildTask("summer-grand-t2", {
        name: "Baseball Winner",
        description: "Win a baseball bet.",
        trigger: { betType: "SINGLE", qualifier: "WIN" },
      }),
      buildTask("summer-grand-t3", {
        name: "High Roller",
        description: "Place a $200+ Rebet Cash wager.",
        trigger: { minBetAmount: "200", qualifier: "ANY_SETTLED" },
      }),
      buildTask("summer-grand-t4", {
        name: "Streak Builder",
        description: "Win four bets in a row.",
        trigger: { qualifier: "WIN", streak: "4" },
      }),
    ],
  }),
  buildChallenge("summer-finale", {
    name: "Heat Wave Finale",
    description: "Closing-week sprint to lock in season rewards.",
    rewards: [
      buildReward("summer-finale-r1", { amount: "150", type: "FREE_CASH_PICK" }),
      buildReward("summer-finale-r2", { amount: "2000", type: "XP" }),
    ],
    tasks: [
      buildTask("summer-finale-t1", {
        name: "Photo Finish",
        description: "Win a P2P duel by 1 unit.",
        trigger: { betType: "SGP", qualifier: "WIN" },
      }),
      buildTask("summer-finale-t2", {
        name: "Coins Conqueror",
        description: "Win a Rebet Coins bet.",
        trigger: {
          betCurrencies: ["REBET_COINS"],
          qualifier: "WIN",
        },
      }),
      buildTask("summer-finale-t3", {
        name: "All-Sports Sweep",
        description: "Win at least one bet across four different sports.",
        trigger: { qualifier: "WIN", sportSpecific: ["NHL", "AHL"] },
      }),
      buildTask("summer-finale-t4", {
        name: "Hot Streak",
        description: "Win five straight Rebet Cash bets.",
        trigger: { qualifier: "WIN", streak: "5" },
      }),
    ],
  }),
];

const SEPTEMBER_2026_CHALLENGES: ReadonlyArray<ChallengeDraft> = [
  buildChallenge("sept-kickoff", {
    name: "Kickoff Challenge",
    description: "Football is back — start strong.",
    rewards: [
      buildReward("sept-kickoff-r1", { amount: "30", type: "FREE_CASH_PICK" }),
      buildReward("sept-kickoff-r2", { amount: "300", type: "XP" }),
    ],
    tasks: [
      buildTask("sept-kickoff-t1", {
        name: "Opening Week",
        description: "Win a bet on an NFL opening week game.",
        trigger: { qualifier: "WIN" },
      }),
      buildTask("sept-kickoff-t2", {
        name: "Spread Sniper",
        description: "Win a straight bet against the spread.",
        trigger: { betType: "SINGLE", qualifier: "WIN" },
      }),
      buildTask("sept-kickoff-t3", {
        name: "Sunday Winner",
        description: "Win a Sunday game bet.",
        trigger: { qualifier: "WIN" },
      }),
    ],
  }),
  buildChallenge("sept-midweek", {
    name: "Midweek Crusher",
    description: "Stay sharp between weekend matchups.",
    rewards: [
      buildReward("sept-midweek-r1", { amount: "60", type: "FREE_CASH_PICK" }),
      buildReward("sept-midweek-r2", { amount: "750", type: "XP" }),
    ],
    tasks: [
      buildTask("sept-midweek-t1", {
        name: "Tuesday Bettor",
        description: "Place a bet on a Tuesday game.",
        trigger: { qualifier: "ANY_SETTLED" },
      }),
      buildTask("sept-midweek-t2", {
        name: "Wednesday Winner",
        description: "Win a midweek parlay.",
        trigger: { betType: "PARLAY", qualifier: "WIN" },
      }),
      buildTask("sept-midweek-t3", {
        name: "Coins Winner",
        description: "Win a Rebet Coins bet.",
        trigger: {
          betCurrencies: ["REBET_COINS"],
          qualifier: "WIN",
        },
      }),
      buildTask("sept-midweek-t4", {
        name: "Underdog Hunter",
        description: "Win an underdog moneyline bet.",
        trigger: { qualifier: "WIN" },
      }),
    ],
  }),
  buildChallenge("sept-finals", {
    name: "Season Finals",
    description: "Cap off September with the headliner challenge.",
    rewards: [
      buildReward("sept-finals-r1", { amount: "120", type: "FREE_CASH_PICK" }),
      buildReward("sept-finals-r2", { amount: "1500", type: "XP" }),
    ],
    tasks: [
      buildTask("sept-finals-t1", {
        name: "Mega Parlay",
        description: "Win a 5-leg parlay.",
        trigger: { betType: "PARLAY", qualifier: "WIN" },
      }),
      buildTask("sept-finals-t2", {
        name: "Big Bettor",
        description: "Place a $50+ bet during the season.",
        trigger: { minBetAmount: "50", qualifier: "ANY_SETTLED" },
      }),
      buildTask("sept-finals-t3", {
        name: "Casino Detour",
        description: "Place 5 casino wagers totalling $25+.",
        trigger: { taskType: "CASINO", minBetAmount: "25" },
      }),
      buildTask("sept-finals-t4", {
        name: "Streak Survivor",
        description: "Win a bet after losing two in a row.",
        trigger: { qualifier: "WIN", streak: "2" },
      }),
      buildTask("sept-finals-t5", {
        name: "Social Shoutout",
        description: "Share a winning ticket to socials.",
        trigger: { taskType: "SOCIALS", qualifier: "WIN" },
      }),
    ],
  }),
];

const FALL_2026_CHALLENGES: ReadonlyArray<ChallengeDraft> = [
  buildChallenge("fall-leaf", {
    name: "Leaf Peeper Bets",
    description: "Get into the autumn rhythm with three easy wins.",
    rewards: [
      buildReward("fall-leaf-r1", { amount: "20", type: "FREE_CASH_PICK" }),
      buildReward("fall-leaf-r2", { amount: "200", type: "XP" }),
    ],
    tasks: [
      buildTask("fall-leaf-t1", {
        name: "Autumn Opener",
        description: "Place your first autumn bet.",
        trigger: { qualifier: "ANY_SETTLED" },
      }),
      buildTask("fall-leaf-t2", {
        name: "Pumpkin Parlay",
        description: "Place a 3-leg parlay during October.",
        trigger: { betType: "PARLAY", qualifier: "ANY_SETTLED" },
      }),
      buildTask("fall-leaf-t3", {
        name: "Crisp Win",
        description: "Win a Rebet Cash bet on a Sunday.",
        trigger: { qualifier: "WIN" },
      }),
    ],
  }),
  buildChallenge("fall-harvest", {
    name: "Harvest Hauler",
    description: "Stack mid-season wins to climb the leaderboard.",
    rewards: [
      buildReward("fall-harvest-r1", { amount: "75", type: "FREE_CASH_PICK" }),
      buildReward("fall-harvest-r2", { amount: "1000", type: "XP" }),
    ],
    tasks: [
      buildTask("fall-harvest-t1", {
        name: "Field Goal",
        description: "Win a straight bet on a low-scoring game.",
        trigger: { betType: "SINGLE", qualifier: "WIN" },
      }),
      buildTask("fall-harvest-t2", {
        name: "Touchdown Trio",
        description: "Win 3 bets in a row on football.",
        trigger: { qualifier: "WIN", streak: "3" },
      }),
      buildTask("fall-harvest-t3", {
        name: "Coin Bowl",
        description: "Win a Rebet Coins bet.",
        trigger: {
          betCurrencies: ["REBET_COINS"],
          qualifier: "WIN",
        },
      }),
      buildTask("fall-harvest-t4", {
        name: "Weekly Bettor",
        description: "Place a bet during the week.",
        trigger: { qualifier: "ANY_SETTLED" },
      }),
    ],
  }),
  buildChallenge("fall-finale", {
    name: "November Showdown",
    description: "Big-stakes finale — for the regulars.",
    rewards: [
      buildReward("fall-finale-r1", { amount: "175", type: "FREE_CASH_PICK" }),
      buildReward("fall-finale-r2", { amount: "2500", type: "XP" }),
    ],
    tasks: [
      buildTask("fall-finale-t1", {
        name: "Thanksgiving Winner",
        description: "Win a Thanksgiving Day game bet.",
        trigger: { qualifier: "WIN" },
      }),
      buildTask("fall-finale-t2", {
        name: "High Stakes",
        description: "Place a $250+ Rebet Cash wager.",
        trigger: { minBetAmount: "250", qualifier: "ANY_SETTLED" },
      }),
      buildTask("fall-finale-t3", {
        name: "Variety Pack",
        description: "Win at least one bet across five different sports.",
        trigger: { qualifier: "WIN", sportSpecific: ["SOCCER", "TENNIS"] },
      }),
      buildTask("fall-finale-t4", {
        name: "Parlay Power Hour",
        description: "Win a 4-leg parlay on Sunday.",
        trigger: { betType: "PARLAY", qualifier: "WIN" },
      }),
      buildTask("fall-finale-t5", {
        name: "Cap It Off",
        description: "Win 5 straight bets to close the season.",
        trigger: { qualifier: "WIN", streak: "5" },
      }),
    ],
  }),
];

// Draft seasons keep deliberately small challenge sets — they reflect
// work-in-progress configurations, not fully fleshed-out lifecycles.
// The `metrics` counts on `MOCK_DRAFT_SEASONS` mirror the totals here so
// the listing card and the details page stay in sync.

const WINTER_2026_DRAFT_CHALLENGES: ReadonlyArray<ChallengeDraft> = [
  buildChallenge("winter-draft-frost", {
    name: "Frosty Opener",
    description: "Kick off the holiday season with a couple of intro bets.",
    rewards: [
      buildReward("winter-draft-frost-r1", { amount: "25", type: "FREE_CASH_PICK" }),
      buildReward("winter-draft-frost-r2", { amount: "250", type: "XP" }),
    ],
    tasks: [
      buildTask("winter-draft-frost-t1", {
        name: "Snowy Start",
        description: "Place your first Winter season bet.",
        trigger: { qualifier: "ANY_SETTLED" },
      }),
      buildTask("winter-draft-frost-t2", {
        name: "Hot Cocoa Combo",
        description: "Place a 2-leg parlay.",
        trigger: { betType: "PARLAY", qualifier: "ANY_SETTLED" },
      }),
    ],
  }),
  buildChallenge("winter-draft-gift", {
    name: "Gift Wrap Wins",
    description: "Build a small win streak heading into the holidays.",
    rewards: [
      buildReward("winter-draft-gift-r1", { amount: "50", type: "FREE_CASH_PICK" }),
      buildReward("winter-draft-gift-r2", { amount: "500", type: "XP" }),
    ],
    tasks: [
      buildTask("winter-draft-gift-t1", {
        name: "Double Down",
        description: "Win two Rebet Cash bets back-to-back.",
        trigger: { qualifier: "WIN", streak: "2" },
      }),
      buildTask("winter-draft-gift-t2", {
        name: "Coin Cracker",
        description: "Win a Rebet Coins wager.",
        trigger: { betCurrencies: ["REBET_COINS"], qualifier: "WIN" },
      }),
    ],
  }),
  buildChallenge("winter-draft-finale", {
    name: "New Year's Eve Showdown",
    description: "Close out the year with one big-ticket win.",
    rewards: [
      buildReward("winter-draft-finale-r1", { amount: "100", type: "FREE_CASH_PICK" }),
    ],
    tasks: [
      buildTask("winter-draft-finale-t1", {
        name: "Countdown Cash",
        description: "Win a $50+ Rebet Cash bet on Dec 31.",
        trigger: { minBetAmount: "50", qualifier: "WIN" },
      }),
    ],
  }),
];

// Completed seasons all share one read-only challenge sample so the
// title-chevron navigation continues to land on a populated details
// page without inflating this module with six near-identical
// challenge sets. The listing's per-card `metrics` counts intentionally
// stay tailored per season — these are mocks, the slight mismatch with
// the details view is acceptable until the seasons API ships its own
// per-season payload.
const COMPLETED_SAMPLE_CHALLENGES: ReadonlyArray<ChallengeDraft> = [
  buildChallenge("completed-sample-opener", {
    name: "Season Opener",
    description: "Get into the season with a couple of warm-up bets.",
    rewards: [
      buildReward("completed-sample-opener-r1", {
        amount: "25",
        type: "FREE_CASH_PICK",
      }),
      buildReward("completed-sample-opener-r2", {
        amount: "250",
        type: "XP",
      }),
    ],
    tasks: [
      buildTask("completed-sample-opener-t1", {
        name: "First Bet",
        description: "Place your first bet of the season.",
        trigger: { qualifier: "ANY_SETTLED" },
      }),
      buildTask("completed-sample-opener-t2", {
        name: "Double Down",
        description: "Win two Rebet Cash bets back-to-back.",
        trigger: { qualifier: "WIN", streak: "2" },
      }),
    ],
  }),
  buildChallenge("completed-sample-grinder", {
    name: "Mid-Season Grinder",
    description: "Stack consistent wins through the middle of the season.",
    rewards: [
      buildReward("completed-sample-grinder-r1", {
        amount: "75",
        type: "FREE_CASH_PICK",
      }),
      buildReward("completed-sample-grinder-r2", {
        amount: "750",
        type: "XP",
      }),
    ],
    tasks: [
      buildTask("completed-sample-grinder-t1", {
        name: "Parlay Pro",
        description: "Win a 3-leg parlay.",
        trigger: { betType: "PARLAY", qualifier: "WIN" },
      }),
      buildTask("completed-sample-grinder-t2", {
        name: "Coin Cracker",
        description: "Win a Rebet Coins wager.",
        trigger: { betCurrencies: ["REBET_COINS"], qualifier: "WIN" },
      }),
    ],
  }),
  buildChallenge("completed-sample-finale", {
    name: "Season Finale",
    description: "Close out the season with a big-ticket win.",
    rewards: [
      buildReward("completed-sample-finale-r1", {
        amount: "150",
        type: "FREE_CASH_PICK",
      }),
    ],
    tasks: [
      buildTask("completed-sample-finale-t1", {
        name: "Big Spender",
        description: "Place a $100+ Rebet Cash bet and win.",
        trigger: { minBetAmount: "100", qualifier: "WIN" },
      }),
      buildTask("completed-sample-finale-t2", {
        name: "Hot Streak",
        description: "Win three Rebet Cash bets in a row.",
        trigger: { qualifier: "WIN", streak: "3" },
      }),
    ],
  }),
];

const NEW_YEAR_2027_DRAFT_CHALLENGES: ReadonlyArray<ChallengeDraft> = [
  buildChallenge("ny-draft-resolution", {
    name: "Resolution Run",
    description: "Build a steady betting habit to kick off the new year.",
    rewards: [
      buildReward("ny-draft-resolution-r1", { amount: "30", type: "FREE_CASH_PICK" }),
      buildReward("ny-draft-resolution-r2", { amount: "300", type: "XP" }),
    ],
    tasks: [
      buildTask("ny-draft-resolution-t1", {
        name: "Fresh Start",
        description: "Place your first bet of 2027.",
        trigger: { qualifier: "ANY_SETTLED" },
      }),
      buildTask("ny-draft-resolution-t2", {
        name: "Weekly Warmup",
        description: "Place a bet in the first week.",
        trigger: { qualifier: "ANY_SETTLED" },
      }),
    ],
  }),
  buildChallenge("ny-draft-kickoff", {
    name: "Kickoff Bonus",
    description: "Reward early-bird players with a win streak target.",
    rewards: [
      buildReward("ny-draft-kickoff-r1", { amount: "75", type: "FREE_CASH_PICK" }),
    ],
    tasks: [
      buildTask("ny-draft-kickoff-t1", {
        name: "First Win",
        description: "Win your first Rebet Cash bet of the year.",
        trigger: { qualifier: "WIN" },
      }),
      buildTask("ny-draft-kickoff-t2", {
        name: "Parlay Primer",
        description: "Win a 2-leg parlay.",
        trigger: { betType: "PARLAY", qualifier: "WIN" },
      }),
    ],
  }),
];

/**
 * Per-season detail data for the Season Details page. Keyed by `SeasonData.id`
 * so the page can resolve a route param straight to the right payload. When
 * the seasons API ships, only this map is replaced — every consumer keeps
 * using `findSeasonDetailsById`.
 */
// Helpers — keep the typed seeding for `schedule` readable when the
// mock map below has to feed `EditSeasonModal`. Day-of-month + ISO
// strings stay explicit rather than computed so a future change to
// `dayjs` defaults can't silently shift the mocks.
const makeDate = (iso: string): Dayjs => dayjs(iso);
const TIME_12_00_AM = dayjs("2000-01-01T00:00");
const TIME_11_59_PM = dayjs("2000-01-01T23:59");

export const SEASON_DETAILS_BY_ID: Record<string, SeasonDetailsData> = {
  [MOCK_ACTIVE_SEASON.id]: {
    season: MOCK_ACTIVE_SEASON,
    mode: "locked",
    general: {
      fullDateRange: "June 1, 2026 (12:00am) - June 31, 2026 (11:59pm)",
      enrolled: 2012,
      avgCompletionPct: 68,
      reportsCount: 12,
      endSeasonEnabled: true,
    },
    sequential: true,
    challenges: MAY_2026_CHALLENGES,
  },
  [MOCK_SCHEDULED_SEASONS[0].id]: {
    season: MOCK_SCHEDULED_SEASONS[0],
    // Up Next is still locked (live in the queue, can't be edited freely).
    mode: "locked",
    general: {
      fullDateRange: "June 1, 2026 (12:00am) - August 31, 2026 (11:59pm)",
      enrolled: 1240,
      avgCompletionPct: 0,
      reportsCount: 0,
      endSeasonEnabled: false,
    },
    sequential: true,
    challenges: SUMMER_2026_CHALLENGES,
  },
  [MOCK_SCHEDULED_SEASONS[1].id]: {
    season: MOCK_SCHEDULED_SEASONS[1],
    mode: "editable",
    general: {
      fullDateRange:
        "September 1, 2026 (12:00am) - September 30, 2026 (11:59pm)",
      enrolled: 0,
      avgCompletionPct: 0,
      reportsCount: 0,
      endSeasonEnabled: false,
      description:
        "Kick off the NFL season with weekly challenges across all sportsbook surfaces.",
      tiers: { free: true, premium: true },
      schedule: {
        autoStartAfter: false,
        startDate: makeDate("2026-09-01"),
        startTime: TIME_12_00_AM,
        endDate: makeDate("2026-09-30"),
        endTime: TIME_11_59_PM,
      },
    },
    sequential: true,
    challenges: SEPTEMBER_2026_CHALLENGES,
  },
  [MOCK_SCHEDULED_SEASONS[2].id]: {
    season: MOCK_SCHEDULED_SEASONS[2],
    mode: "editable",
    general: {
      fullDateRange:
        "October 1, 2026 (12:00am) - November 30, 2026 (11:59pm)",
      enrolled: 0,
      avgCompletionPct: 0,
      reportsCount: 0,
      endSeasonEnabled: false,
      description: "",
      tiers: { free: true, premium: false },
      schedule: {
        autoStartAfter: false,
        startDate: makeDate("2026-10-01"),
        startTime: TIME_12_00_AM,
        endDate: makeDate("2026-11-30"),
        endTime: TIME_11_59_PM,
      },
    },
    sequential: false,
    challenges: FALL_2026_CHALLENGES,
  },
  [MOCK_DRAFT_SEASONS[0].id]: {
    season: MOCK_DRAFT_SEASONS[0],
    mode: "editable",
    general: {
      fullDateRange:
        "December 1, 2026 (12:00am) - December 31, 2026 (11:59pm)",
      enrolled: 0,
      avgCompletionPct: 0,
      reportsCount: 0,
      endSeasonEnabled: false,
      description:
        "Holiday-themed promo season — draft pending challenge and reward sign-off from marketing.",
      tiers: { free: true, premium: true },
      schedule: {
        autoStartAfter: false,
        startDate: makeDate("2026-12-01"),
        startTime: TIME_12_00_AM,
        endDate: makeDate("2026-12-31"),
        endTime: TIME_11_59_PM,
      },
    },
    sequential: false,
    challenges: WINTER_2026_DRAFT_CHALLENGES,
  },
  [MOCK_DRAFT_SEASONS[1].id]: {
    season: MOCK_DRAFT_SEASONS[1],
    mode: "editable",
    general: {
      fullDateRange:
        "January 1, 2027 (12:00am) - January 31, 2027 (11:59pm)",
      enrolled: 0,
      avgCompletionPct: 0,
      reportsCount: 0,
      endSeasonEnabled: false,
      description: "",
      tiers: { free: true, premium: false },
      schedule: {
        autoStartAfter: false,
        startDate: makeDate("2027-01-01"),
        startTime: TIME_12_00_AM,
        endDate: makeDate("2027-01-31"),
        endTime: TIME_11_59_PM,
      },
    },
    sequential: false,
    challenges: NEW_YEAR_2027_DRAFT_CHALLENGES,
  },
  // Completed seasons all render the locked Details view (same as the
  // Active season) so the title-chevron navigation continues to work
  // from the Completed column on the listing. They share one sample
  // challenge set (`COMPLETED_SAMPLE_CHALLENGES`) — see the comment on
  // that constant for the rationale. The per-season metrics on the
  // listing card stay tailored; only the details view is shared.
  [MOCK_COMPLETED_SEASONS[0].id]: {
    season: MOCK_COMPLETED_SEASONS[0],
    mode: "locked",
    general: {
      fullDateRange: "April 1, 2026 (12:00am) - April 30, 2026 (11:59pm)",
      enrolled: 1840,
      avgCompletionPct: 72,
      reportsCount: 14,
      endSeasonEnabled: false,
    },
    sequential: true,
    challenges: COMPLETED_SAMPLE_CHALLENGES,
  },
  [MOCK_COMPLETED_SEASONS[1].id]: {
    season: MOCK_COMPLETED_SEASONS[1],
    mode: "locked",
    general: {
      fullDateRange: "March 1, 2026 (12:00am) - March 31, 2026 (11:59pm)",
      enrolled: 1620,
      avgCompletionPct: 65,
      reportsCount: 11,
      endSeasonEnabled: false,
    },
    sequential: true,
    challenges: COMPLETED_SAMPLE_CHALLENGES,
  },
  [MOCK_COMPLETED_SEASONS[2].id]: {
    season: MOCK_COMPLETED_SEASONS[2],
    mode: "locked",
    general: {
      fullDateRange:
        "February 1, 2026 (12:00am) - February 28, 2026 (11:59pm)",
      enrolled: 1450,
      avgCompletionPct: 58,
      reportsCount: 9,
      endSeasonEnabled: false,
    },
    sequential: true,
    challenges: COMPLETED_SAMPLE_CHALLENGES,
  },
  [MOCK_COMPLETED_SEASONS[3].id]: {
    season: MOCK_COMPLETED_SEASONS[3],
    mode: "locked",
    general: {
      fullDateRange:
        "January 1, 2026 (12:00am) - January 31, 2026 (11:59pm)",
      enrolled: 1320,
      avgCompletionPct: 61,
      reportsCount: 10,
      endSeasonEnabled: false,
    },
    sequential: true,
    challenges: COMPLETED_SAMPLE_CHALLENGES,
  },
  [MOCK_COMPLETED_SEASONS[4].id]: {
    season: MOCK_COMPLETED_SEASONS[4],
    mode: "locked",
    general: {
      fullDateRange:
        "December 1, 2025 (12:00am) - December 31, 2025 (11:59pm)",
      enrolled: 2210,
      avgCompletionPct: 74,
      reportsCount: 18,
      endSeasonEnabled: false,
    },
    sequential: true,
    challenges: COMPLETED_SAMPLE_CHALLENGES,
  },
  [MOCK_COMPLETED_SEASONS[5].id]: {
    season: MOCK_COMPLETED_SEASONS[5],
    mode: "locked",
    general: {
      fullDateRange:
        "November 1, 2025 (12:00am) - November 30, 2025 (11:59pm)",
      enrolled: 1735,
      avgCompletionPct: 63,
      reportsCount: 12,
      endSeasonEnabled: false,
    },
    sequential: true,
    challenges: COMPLETED_SAMPLE_CHALLENGES,
  },
};

/**
 * Resolve a `SeasonDetailsData` by id. Returns `undefined` when no mock
 * exists so the Details page can render its "not found" empty state
 * instead of crashing. Centralised so the future API swap is one edit.
 */
export const findSeasonDetailsById = (
  id: string | undefined,
): SeasonDetailsData | undefined => {
  if (!id) return undefined;
  return SEASON_DETAILS_BY_ID[id];
};
